/* eslint-disable react/prop-types */
import { CIActionGuard, useUserPermission } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { requiredAuthPermission } from '../../../common/constants/UserPermissionConstants';
import { journeyBuilderRoutes } from '../../../journey-builder/constants/JdRoutes';
import EventIcon from '../../components/icons/EventIcon';
import FolderIcon from '../../components/icons/FolderIcon';
import JourneyIcon from '../../components/icons/JourneyIcon';
import ServiceIcon from '../../components/icons/ServiceIcon';
import { CountStructure } from '../../components/utility/type-defs';
import { STATUS_TYPES } from '../../constants/CommonConstants';
import classes from './JourneyCard.module.scss';

const JourneyCard: React.FC<{ counts: CountStructure, typeOfCard: string }> = ({ counts, typeOfCard = '' }) => {
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  const navigate = useNavigate();
  const [showPendingApprovalBtn, setShowPendingApprovalBtn] = useState<boolean>(false);
  const [showReturnedBtn, setShowReturnedBtn] = useState<boolean>(false);

  useEffect(() => {
    const servicePerm = (typeOfCard.toLocaleLowerCase() === 'service' && CIActionGuard([requiredAuthPermission.Services.approve], permissionObject.permission));
    const eventPerm = (typeOfCard.toLocaleLowerCase() === 'event' && CIActionGuard([requiredAuthPermission.Events.approve], permissionObject.permission));
    const journeyPerm = (typeOfCard.toLocaleLowerCase() === 'journey' && CIActionGuard([requiredAuthPermission.JD.approve], permissionObject.permission));

    const servicePermForReturn = (typeOfCard.toLocaleLowerCase() === 'service' && CIActionGuard([requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve], permissionObject.permission));
    const eventPermForReturn = (typeOfCard.toLocaleLowerCase() === 'event' && CIActionGuard([requiredAuthPermission.Events.edit, requiredAuthPermission.Events.approve], permissionObject.permission));
    const journeyPermForReturn = (typeOfCard.toLocaleLowerCase() === 'journey' && CIActionGuard([requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve], permissionObject.permission));
    if (servicePerm || eventPerm || journeyPerm) {
      setShowPendingApprovalBtn(true);
    }
    if (servicePermForReturn || eventPermForReturn || journeyPermForReturn) {
      setShowReturnedBtn(true);
    }
  }, [typeOfCard]);

  const pendingApprovalBtnHandler = () => {
    if (typeOfCard.toLocaleLowerCase() === 'service') {
      navigate({
        pathname: '/control-plane/services',
        search: `?status=${STATUS_TYPES.PENDING_APPROVAL}&page=0`
      });
    } else if (typeOfCard.toLocaleLowerCase() === 'event') {
      navigate({
        pathname: '/control-plane/events',
        search: `?status=${STATUS_TYPES.PENDING_APPROVAL}&page=0`
      });
    } else if (typeOfCard.toLocaleLowerCase() === 'journey') {
      navigate({
        pathname: journeyBuilderRoutes.viewJourney,
        search: `?status=${STATUS_TYPES.PENDING_APPROVAL}&page=0`
      });
    }
  };

  const returnedBtnHandler = () => {
    if (typeOfCard.toLocaleLowerCase() === 'service') {
      navigate({
        pathname: '/control-plane/services',
        search: `?status=${STATUS_TYPES.RETURNED}&page=0`
      });
    } else if (typeOfCard.toLocaleLowerCase() === 'event') {
      navigate({
        pathname: '/control-plane/events',
        search: `?status=${STATUS_TYPES.RETURNED}&page=0`
      });
    } else if (typeOfCard.toLocaleLowerCase() === 'journey') {
      navigate({
        pathname: journeyBuilderRoutes.viewJourney,
        search: `?status=${STATUS_TYPES.RETURNED}&page=0`
      });
    }
  };

  return (
    <div className={classes.container}>
      <Row className={classes['container-row-main-header']}>
        <Col xs={4} sm={2} className={classes['container-text']}>
          {typeOfCard.toLocaleLowerCase() === 'service' && <span className={classes['icon-bg']} data-testid="service-icon"><ServiceIcon /></span>}
          {typeOfCard.toLocaleLowerCase() === 'event' && <span className={classes['icon-bg']} data-testid="event-icon"><EventIcon /></span>}
          {typeOfCard.toLocaleLowerCase() === 'journey' && <span className={classes['icon-bg']} data-testid="journey-icon"><JourneyIcon /></span>}
        </Col>
        <Col xs={8} sm={6} className={classes['main-header']}>
          {typeOfCard.toLocaleLowerCase() === 'service' && translate('journey_card.service_header')}
          {typeOfCard.toLocaleLowerCase() === 'event' && translate('journey_card.event_header')}
          {typeOfCard.toLocaleLowerCase() === 'journey' && translate('journey_card.journey_header')}
        </Col>
        <Col xs={12} sm={4} className={classes['view-all-link']}>
          {typeOfCard.toLocaleLowerCase() === 'service' && CIActionGuard([requiredAuthPermission.Services.view, requiredAuthPermission.Services.edit, requiredAuthPermission.Services.approve], permissionObject.permission) ? <Link to="/control-plane/services" state={{ creatreNewEventButtonClicked: true }} className={classes['view-all-link-text']}>{translate('journey_card.view_all_link_text')}</Link> : ''}
          {typeOfCard.toLocaleLowerCase() === 'event' && CIActionGuard([requiredAuthPermission.Events.view, requiredAuthPermission.Events.edit, requiredAuthPermission.Events.approve], permissionObject.permission) ? <Link to="/control-plane/events" state={{ creatreNewEventButtonClicked: true, index: null, from: '/control-plane/dashboard' }} className={classes['view-all-link-text']}>{translate('journey_card.view_all_link_text')}</Link> : ''}
          {typeOfCard.toLocaleLowerCase() === 'journey' && CIActionGuard([requiredAuthPermission.JD.view, requiredAuthPermission.JD.edit, requiredAuthPermission.JD.approve], permissionObject.permission) ? <Link to={journeyBuilderRoutes.viewJourney} className={classes['view-all-link-text']}>{translate('journey_card.view_all_link_text')}</Link> : ''}
        </Col>
      </Row>
      <Row className={classes['container-row-subheader']}>
        <Col xs={6} sm={4} className={classes['sub-header']}>
          {translate('journey_card.draft_tag')} <div className={classes['no-of-count']}>({counts.draft})</div>
        </Col>
        <Col xs={6} sm={4} className={classes['sub-header']}>
          {translate('journey_card.active_tag')} <div className={classes['no-of-count']}>({counts.active})</div>
        </Col>
        <Col xs={6} sm={4} className={classes['sub-header']}>
          {translate('journey_card.inactive_tag')} <div className={classes['no-of-count']}>({counts.inActive})</div>
        </Col>
      </Row>
      <Row className={classes['container-row-action-item']}>
        <Col sm={12} className={classes['action-item-header']}>
          {translate('journey_card.action_items')}
        </Col>
      </Row>
      {(counts.pendingApproval && showPendingApprovalBtn) ? (
        <Row className={classes['container-row-button']}>
          <Col sm={12}>
            <Button
              variant="tertiary"
              className={classes['container-button']}
              onClick={pendingApprovalBtnHandler}
            >
              <span className={classes['no-of-count-in-button']}>{counts.pendingApproval}</span>
              <span className={classes['button-text']}>{translate('journey_card.pending_approval_tag')}</span>
              <span className={classes['arrow-button']}><i className="bi bi-arrow-right" /></span>
            </Button>
          </Col>
        </Row>) : ''}
      {(counts.returned && showReturnedBtn) ? (
        <Row className={classes['container-row-button']}>
          <Col sm={12}>
            <Button
              variant="tertiary"
              className={classes['container-button']}
              onClick={returnedBtnHandler}
              data-testid="return-button"
            >
              <span className={classes['no-of-count-in-button']}>{counts.returned}</span>
              <span className={classes['button-text']}>{translate('journey_card.returned_tag')}</span>
              <span className={classes['arrow-button']}><i className="bi bi-arrow-right" /></span>
            </Button>
          </Col>
        </Row>) : ''}
      {((!counts.pendingApproval && !counts.returned) || !(showPendingApprovalBtn && showReturnedBtn)) && (
        <Row>
          <Col sm={12} className={classes['container-row-no-action-item']}>
            <div className={classes['container-text']}>{translate('journey_card.no_action')}</div>
            <FolderIcon />
          </Col>
        </Row>)}
    </div>
  );
};
export default JourneyCard;
